<template>
  <overlay-component
    :width="300"
    :no-overlay="noOverlay"
    @visible="hover = true"
    @hidden="hover = false"
  >
    <component
      :is="itemComponentProps.is"
      v-bind="itemComponentProps"
    >
      <div class="flex items-center">
        <div
          class="overflow-hidden flex-grow-0 flex-shrink-0 mr-3 rounded-full"
          :class="picSize ? `w-${picSize} h-${picSize}` : 'md:w-12 md:h-12 w-8 h-8'"
        >
          <img
            v-if="model && model.imageUrl"
            :src="model.imageUrl"
            :class="picSize ? `w-${picSize} h-${picSize}` : 'md:w-12 md:h-12 w-8 h-8'"
          >
          <img
            v-else
            src="@/assets/image-placeholder.jpg"
            :class="picSize ? `w-${picSize} h-${picSize}` : 'md:w-12 md:h-12 w-8 h-8'"
          >
        </div>
        <div>
          <div
            v-if="model && model.name"
            class="overflow-hidden text-sm font-medium dark:text-white overflow-ellipsis whitespace-nowrap"
            style="max-width: 10rem"
          >
            {{ model.name }}
            <i
              v-if="model.isVerified"
              class="text-blue-400 fas fa-badge-check"
              title="Verified on Opensea"
            />
          </div>

          <div v-else>
            <loading-placeholder
              class="h-4"
              :width="130"
              :height="16"
              :speed="2"
              primary-color="#f3f3f3"
              secondary-color="#ecebeb"
            >
              <rect
                x="0"
                y="0"
                rx="3"
                ry="3"
                width="130"
                height="16"
              />
            </loading-placeholder>
          </div>

          <div
            v-if="!mini"
            class="flex space-x-1 text-xs dark:text-white opacity-75"
          >
            <slot
              name="extra-content"
              :item="model"
            >
              <span>{{ moment(model.contractCreatedTimestamp).fromNow(true) }}</span>
              <span>·</span>
              <span>{{ formatNumber(model.totalSupply, 0, false) }} tokens</span>
            </slot>
          </div>
        </div>
      </div>
    </component>
    <template #overlay-content>
      <div class="mt-4">
        <ui-link :to="{name: 'Collections.Show', params: { contractAddress: model.id, currentTab: 'items' }}">
          <ui-badge
            v-if="!isRevealedQueryLoading && isRevealed"
            variant="indigo"
          >
            Revealed
          </ui-badge>
          <ui-badge
            v-else-if="!isRevealedQueryLoading"
            variant="yellow"
          >
            Unrevealed
          </ui-badge>
          <loading-placeholder
            v-else
            class="inline-block"
            :style="{width: 72 + 'px', height: 20 + 'px'}"
            :width="72"
            :height="20"
          >
            <rect
              x="0"
              y="0"
              rx="10"
              ry="10"
              :width="72"
              :height="20"
            />
          </loading-placeholder>
        </ui-link>
      </div>

      <div
        v-if="hover"
        class="mt-4"
      >
        <div class="flex justify-between items-center space-x-2">
          <div>
            <toggle-favorite
              :id="model.id"
              model="Collection"
            >
              <template #default="{isFavorite, toggleFavorite, isToggling, isLoading}">
                <ui-button
                  size="xs"
                  class="col-span-2"
                  :is-loading="isToggling || isLoading"
                  type="button"
                  variant="white"
                  @click="toggleFavorite"
                >
                  <div>
                    <i
                      class="fa-heart"
                      :class="isFavorite ? 'fas text-red-500' : 'far'"
                    />
                  </div>
                  <span class="ml-2">{{ isFavorite ? 'Unfavorite' : 'Favorite' }}</span>
                </ui-button>
              </template>
            </toggle-favorite>
          </div>
          <collection-links :collection="model" />
        </div>
      </div>
    </template>
  </overlay-component>
</template>

<script>
import {
  defineComponent,
  computed,
  toRefs,
  ref,
} from "vue";
import { formatNumber } from "@/utils/filters";
import { useQuery } from "@vue/apollo-composable";
import collectionListItemQuery from "@/graphql/collection/queries/collectionListItem.query.gql";
import collectionIsRevealedQuery from "@/graphql/collection/queries/collectionIsRevealed.query.gql";
import moment from "moment-timezone";

import UiLink from "@/components/ui/UiLink";
import UiButton from '../ui/UiButton.vue';
import ToggleFavorite from '../ToggleFavorite.vue'
import UiBadge from '../ui/UiBadge.vue';
import LoadingPlaceholder from '../LoadingPlaceholder.vue';
import CollectionLinks from './CollectionLinks.vue';
import OverlayComponent from '../OverlayComponent.vue';

export default defineComponent({
  name: "CollectionListItem",
  components: { UiLink, UiButton, ToggleFavorite, LoadingPlaceholder, UiBadge, CollectionLinks, OverlayComponent },
  props: {
    collection: undefined,
    mini: undefined,
    address: undefined,
    picSize: undefined,
    targetTab: {
      type: String,
      default: 'analytics'
    },
    noOverlay: {
      default: false,
      type: Boolean
    },
    noLink: {
      default: false,
      type: Boolean,
    }
  },
  setup(props) {
    const {
      address,
      collection,
    } = toRefs(props);
    const hover = ref(false);

    const {
      result: collectionListItemResult,
    } = useQuery(
      collectionListItemQuery,
      () => ({ address: address.value }),
      () => ({ enabled: Boolean(address.value && !collection.value?.id) })
    );

    const model = computed(() => {
      const collectionData = collectionListItemResult.value?.collection; 

      if (collectionData) {
        return collectionData;
      }

      return collection.value;
    });

    const {
      result: isRevealedResult,
      loading: isRevealedQueryLoading,
    } = useQuery(
      collectionIsRevealedQuery,
      () => ({ address: model.value?.id }),
      () => ({ enabled: Boolean(hover.value && model.value?.id) }),
    );

    const isRevealed = computed(() => {
      return isRevealedResult.value?.collection.isRevealed;
    });

    return {
      model,
      hover,
      isRevealed,
      isRevealedQueryLoading,
      formatNumber,
      moment,
    }
  },
  computed: {
    itemComponentProps() {
      if (this.noLink === false) {
        return {
          is: UiLink,
          to: {name: 'Collections.Show', params: {contractAddress: this.model?.id || this.address, currentTab: this.targetTab}},
        }
      }
      return {
        is: 'div',
      }
    }
  }
});
</script>

<style scoped>
  .collection-list-item__overlay {
    margin: calc(-0.5rem - 1px);
    width: calc(100% + 1rem + 1px);
  }
  .bg-inherit {
    background-color: inherit;
  }
</style>